import dayjs from "dayjs";

import { FilterItem } from "../JobMultiFilterButton";
import { DateRangeQueryParam } from "./DateRangeFilter";

const standardQueryStringToColumnMap: { [key: string]: string } = {
  source: "source",
};

const csvQueryStringToColumnMap: { [key: string]: string } = {
  jobids: "_id",
  creativeids: "creative_id",
  creativerenditionids: "source_creative_rendition_id",
  networkids: "network_id",
};

const multiSelectQueryStringToColumnMap: { [key: string]: string } = {
  status: "status",
  setid: "set_id",
};

const constructDate = (date: string | null, time: string | null): dayjs.Dayjs => {
  return dayjs.utc(`${date}T${time}Z`).local();
};

let uniqueNumberCounter = 0;
const generateUniqueNumber = () => {
  const now = Date.now();
  uniqueNumberCounter = (uniqueNumberCounter + 1) % 1000; // Reset counter after 1000
  return Number(`${now}${uniqueNumberCounter.toString().padStart(3, "0")}`);
};

const populateMultiFilterFromSearchParams = (searchParams: URLSearchParams): FilterItem[] => {
  const filterItems: FilterItem[] = [];
  const processedFields = new Set<string>();

  for (const [param, value] of Array.from(searchParams.entries())) {
    // Handle standard query params
    const standardField = standardQueryStringToColumnMap[param];
    if (standardField && !processedFields.has(standardField)) {
      filterItems.push(createStandardFilterItem(standardField, value));
      processedFields.add(standardField);
      continue;
    }

    // Handle csv values query params
    const csvField = csvQueryStringToColumnMap[param];
    if (csvField && !processedFields.has(csvField)) {
      filterItems.push(createCsvFilterItem(csvField, value));
      processedFields.add(csvField);
      continue;
    }

    // Handle multiple select query params
    const multiSelectField = multiSelectQueryStringToColumnMap[param];
    if (multiSelectField && !processedFields.has(multiSelectField)) {
      filterItems.push(createMultiSelectFilterItem(multiSelectField, searchParams.getAll(param)));
      processedFields.add(multiSelectField);
      continue;
    }

    // Handle date range query params
    const isDateRangeValid =
      param === "start" &&
      searchParams.has("starttime") &&
      searchParams.has("end") &&
      searchParams.has("endtime");

    if (isDateRangeValid && !processedFields.has("start")) {
      filterItems.push(
        createDateRangeFilterItem(
          "created_date",
          constructDate(searchParams.get("start"), searchParams.get("starttime")),
          constructDate(searchParams.get("end"), searchParams.get("endtime")),
        ),
      );
      processedFields.add("start");
    }
  }

  return filterItems;
};

const createStandardFilterItem = (field: string, value: string): FilterItem => {
  return {
    id: generateUniqueNumber(),
    field: field,
    operator: "equals",
    value: value,
    isValid: true,
  };
};

const createCsvFilterItem = (field: string, value: string): FilterItem => {
  return {
    id: generateUniqueNumber(),
    field: field,
    operator: "equals",
    value: value.replace(/,+/g, ", "),
    isValid: true,
  };
};

const createMultiSelectFilterItem = (field: string, values: string[]): FilterItem => {
  return {
    id: generateUniqueNumber(),
    field: field,
    operator: "contains",
    value: values,
    isValid: true,
  };
};

const createDateRangeFilterItem = (field: string, start: dayjs.Dayjs, end: dayjs.Dayjs): FilterItem => {
  return {
    id: generateUniqueNumber(),
    field: field,
    operator: "between",
    value: [start, end],
    isValid: true,
  };
};

const getFilterQueryString = (queryParamKey: string) => (value: string) =>
  `${queryParamKey}=${encodeURIComponent(value)}`;

const getCsvFilterQueryString = (queryParamKey: string) => (value: string) =>
  `${queryParamKey}=${encodeURIComponent(value.replaceAll(/\s+/g, ""))}`;

const getDateRangeFilterQueryString = (dateRangeQueryParam: DateRangeQueryParam) => (dates: Date[]) => {
  if (Array.isArray(dates) && dates.length === 2) {
    const startDate = dayjs(dates[0]).utc();
    const endDate = dayjs(dates[1]).utc();

    return `${dateRangeQueryParam.startDate}=${startDate.format("YYYY-MM-DD")}&${dateRangeQueryParam.startTime}=${startDate.format("HH:mm:ss")}&${dateRangeQueryParam.endDate}=${endDate.format("YYYY-MM-DD")}&${dateRangeQueryParam.endTime}=${endDate.format("HH:mm:ss")}`;
  }

  return "";
};

const getMultiSelectFilterQueryString = (queryParamKey: string) => (values: string[]) =>
  values.map((value) => `${queryParamKey}=${encodeURIComponent(value)}`).join("&");

export {
  generateUniqueNumber,
  getCsvFilterQueryString,
  getDateRangeFilterQueryString,
  getFilterQueryString,
  getMultiSelectFilterQueryString,
  populateMultiFilterFromSearchParams,
};
